import { CopyOutlined } from "@ant-design/icons";
import { App, Button, Flex } from "antd";
import dayjs from "dayjs";
import { FC, MouseEvent, useMemo } from "react";
import { displayDateFormat } from "tm-shared/constants";
import { CheckoutOrder, OrderType } from "tm-shared/types";
import { formatUAHNarrow } from "tm-shared/utils";

import { useAppSelector, useIgnoreRemainingsSelector } from "@/hooks/redux";
import { useCheckoutDetailsSelector } from "@/hooks/redux/useProgressSelectors";
import { useCheckoutContext } from "@/pages/Checkout/Checkout.context";
import { PrimaryText, SecondaryText } from "@/shared/ui/Typography";
import { copyObjectToClipboard } from "@/utils";

interface ICheckoutHeader {
    checkoutOrder: CheckoutOrder;
}

export const CheckoutHeader: FC<ICheckoutHeader> = ({ checkoutOrder }) => {
    const { notification } = App.useApp();

    const userId = useAppSelector((s) => s.user.id);
    const phone = useAppSelector((s) => s.user.phone);
    const team = useAppSelector((s) => s.manager.team);
    const activeSchedule = useAppSelector((s) => s.manager.activeSchedule);

    const { capabilities } = useCheckoutContext();

    const {
        id: invoiceId,
        orderId,
        contragent,
        address,
        creationDate,
        orderType,
        pointId,
        paymentForm,
        closed,
        isCarrier: orderIsCarrier,
        isSelfDelivery: orderIsSelfDelivery,
        isCredit,
    } = checkoutOrder;

    const currentDetails = useCheckoutDetailsSelector(orderId);
    const ignoreRemainings =
        (useIgnoreRemainingsSelector(orderId) ?? []).length > 0;

    const isCarrier = closed ? orderIsCarrier : currentDetails?.isCarrier;
    const isSelfDelivery = closed
        ? orderIsSelfDelivery
        : currentDetails?.isSelfPickup;
    const isPayOnDelivery = closed
        ? !isCredit
        : currentDetails?.isPayOnDelivery;

    const displayInvoiceId = invoiceId || `TM-${orderId}`;

    const formattedDate = dayjs(creationDate).isValid()
        ? dayjs(creationDate).format(displayDateFormat.timeWithSeconds)
        : "-";

    const totalSum = useMemo(
        () =>
            Object.values(checkoutOrder.products).reduce(
                (sum, p) => sum + p.price * p.count,
                0,
            ),
        [checkoutOrder.products],
    );

    const clipboardData = useMemo(() => {
        const agent = team?.members?.find(
            (member) => member.userId === activeSchedule?.userId,
        );
        const agentContact =
            capabilities.canCopyAgentContact && agent
                ? { userId: agent.userId, phone: agent.userInfo.phone }
                : { userId, phone };

        return {
            invoiceId,
            orderId,
            pointId,
            ...agentContact,
            amount: totalSum.toFixed(2),
            closed,
            paymentForm,
            context: {
                isCarrier,
                isSelfDelivery,
                isPayOnDelivery,
                ignoreRemainings,
            },
        };
    }, [
        activeSchedule?.userId,
        capabilities.canCopyAgentContact,
        invoiceId,
        orderId,
        pointId,
        totalSum,
        closed,
        paymentForm,
        ignoreRemainings,
        isCarrier,
        isSelfDelivery,
        isPayOnDelivery,
        phone,
        team?.members,
        userId,
    ]);

    const handleCopyClick = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        copyObjectToClipboard(clipboardData, notification);
    };

    return (
        <Flex vertical gap={8}>
            <Flex justify="space-between" align="center">
                <PrimaryText fontSize={16}>{displayInvoiceId}</PrimaryText>

                <Button
                    type="text"
                    shape="circle"
                    icon={<CopyOutlined style={{ fontSize: 20 }} />}
                    onClick={handleCopyClick}
                />
            </Flex>

            <SecondaryText lineHeight="16px">
                &quot;{contragent || "-"}&quot;
            </SecondaryText>

            <SecondaryText lineHeight="16px">{address || "-"}</SecondaryText>

            <Flex align="center" justify="space-between" gap={8}>
                <SecondaryText>{formattedDate}</SecondaryText>

                {orderType !== OrderType.R && (
                    <PrimaryText textColor="accent" underline>
                        {formatUAHNarrow(totalSum)}
                    </PrimaryText>
                )}
            </Flex>
        </Flex>
    );
};
